import React, { useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { formatUtcToLocal, safeFormatDate } from '../../utils/format'
import {
  MATCH_PROPOSAL_STATUS,
  MATCH_PROPOSAL_STATUS_LABELS,
} from '../../utils/matchStatus'
import './DayDetailPanel.css'

/**
 * Creates a NaN-safe time-based sorting comparator
 * @param {string} timeField - The field name containing the time value
 * @returns {Function} Comparator function for array.sort()
 */
const createTimeSorter = (timeField) => (a, b) => {
  const timeA = new Date(a[timeField]).getTime()
  const timeB = new Date(b[timeField]).getTime()
  // Handle invalid dates by placing them at the end
  if (isNaN(timeA) && isNaN(timeB)) return 0
  if (isNaN(timeA)) return 1
  if (isNaN(timeB)) return -1
  return timeA - timeB
}

/**
 * Creates interactive item props for keyboard/click handlers
 * @param {Function|null} onClick - Click handler function
 * @param {*} data - Data to pass to the click handler
 * @returns {Object} Props object with role, tabIndex, onClick, onKeyDown
 */
const getInteractiveItemProps = (onClick, data) => {
  if (!onClick) return {}

  return {
    role: 'button',
    tabIndex: 0,
    onClick: () => onClick(data),
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onClick(data)
      }
    },
  }
}

/**
 * DayDetailPanel - Displays detailed time slots, proposals, and matches for a selected day
 *
 * @param {Object} props
 * @param {Date|null} props.selectedDate - The currently selected date
 * @param {boolean} props.isClosing - Whether the panel is closing (for animation)
 * @param {Array} props.availability - Array of availability objects for the selected day
 * @param {Array} props.opponentAvailability - Array of opponent availability objects for the selected day
 * @param {Array} props.unplayedOpponents - Array of unplayed opponents for name lookup
 * @param {Array} props.proposals - Array of proposal objects for the selected day
 * @param {Array} props.matches - Array of match objects for the selected day
 * @param {Function} props.onClose - Callback to close the panel
 * @param {Function} props.onAddAvailability - Callback to add new availability
 * @param {Function} props.onEditAvailability - Callback when availability is clicked (receives id)
 * @param {Function} props.onOpponentAvailabilityClick - Callback when opponent availability is clicked (receives opponent availability object)
 * @param {Function} props.onProposalAction - Callback when proposal is clicked (receives proposal)
 * @param {Function} props.onMatchClick - Callback when match is clicked (receives match)
 */
function DayDetailPanel({
  selectedDate,
  isClosing = false,
  availability = [],
  opponentAvailability = [],
  unplayedOpponents = [],
  proposals = [],
  matches = [],
  onClose,
  onAddAvailability,
  onEditAvailability,
  onOpponentAvailabilityClick,
  onProposalAction,
  onMatchClick,
}) {
  // Add keyboard event listener for Escape key
  // Only handle Escape when the panel is actually rendered (selectedDate is not null)
  useEffect(() => {
    if (!selectedDate) return

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && onClose) {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [selectedDate, onClose])

  // Sort availability by start_time chronologically
  const sortedAvailability = useMemo(() => {
    return [...availability].sort(createTimeSorter('start_time'))
  }, [availability])

  // Sort opponent availability by start_time chronologically
  const sortedOpponentAvailability = useMemo(() => {
    return [...opponentAvailability].sort(createTimeSorter('start_time'))
  }, [opponentAvailability])

  // Sort proposals by proposed_time chronologically
  const sortedProposals = useMemo(() => {
    return [...proposals].sort(createTimeSorter('proposed_time'))
  }, [proposals])

  // Sort matches by match_datetime chronologically
  const sortedMatches = useMemo(() => {
    return [...matches].sort(createTimeSorter('match_datetime'))
  }, [matches])

  const hasAnyItems =
    sortedAvailability.length > 0 ||
    sortedOpponentAvailability.length > 0 ||
    sortedProposals.length > 0 ||
    sortedMatches.length > 0

  if (!selectedDate) return null

  const dateDisplay = safeFormatDate(selectedDate, 'EEEE, MMM d, yyyy')

  // Helper function to get opponent name
  const getOpponentName = (opponentUserId) => {
    const opponent = unplayedOpponents.find(
      (opp) => opp.user_id === opponentUserId,
    )
    if (opponent) {
      return `${opponent.first_name} ${opponent.last_name}`
    }
    return 'Unknown Opponent'
  }

  const getProposalStatusBadge = (status) => {
    const statusClassMap = {
      [MATCH_PROPOSAL_STATUS.PENDING]: 'pending',
      [MATCH_PROPOSAL_STATUS.ACCEPTED]: 'accepted',
      [MATCH_PROPOSAL_STATUS.DECLINED]: 'declined',
      [MATCH_PROPOSAL_STATUS.SCHEDULED]: 'accepted', // Use same styling as accepted
      [MATCH_PROPOSAL_STATUS.EXPIRED]: 'declined', // Use same styling as declined
    }

    const label = MATCH_PROPOSAL_STATUS_LABELS[status] || status
    const className = statusClassMap[status] || status

    return (
      <span className={`fl__status-badge fl__status-badge--${className}`}>
        {label}
      </span>
    )
  }

  return (
    <div
      className={`fl__day-detail-panel ${isClosing ? 'closing' : ''}`}
      data-testid="day-detail-panel"
    >
      <div className="fl__day-detail-panel__header">
        <h3 className="fl__day-detail-panel__title">{dateDisplay}</h3>
        <button
          className="fl__day-detail-panel__close-button"
          onClick={onClose}
          aria-label="Close day details"
        >
          ×
        </button>
      </div>

      <div className="fl__day-detail-panel__content">
        {!hasAnyItems && (
          <div className="fl__day-detail-panel__empty">
            <p>No items scheduled for this day</p>
            <p>Click "+ Add Availability" below to get started</p>
          </div>
        )}

        {hasAnyItems && sortedAvailability.length === 0 && (
          <div className="fl__day-detail-panel__section">
            <h4 className="fl__day-detail-panel__section-title">
              Availability (0)
            </h4>
            <p className="fl__day-detail-panel__no-items">
              No availability set
            </p>
          </div>
        )}

        {sortedAvailability.length > 0 && (
          <div className="fl__day-detail-panel__section">
            <h4 className="fl__day-detail-panel__section-title">
              Availability ({sortedAvailability.length})
            </h4>
            <div className="fl__day-detail-panel__items">
              {sortedAvailability.map((item) => {
                const isInteractive = !!onEditAvailability
                const itemProps = getInteractiveItemProps(
                  onEditAvailability,
                  item.id,
                )

                return (
                  <div
                    key={item.id}
                    className={`fl__day-detail-panel__item fl__day-detail-panel__item--availability${
                      isInteractive
                        ? ' fl__day-detail-panel__item--interactive'
                        : ''
                    }`}
                    data-testid="availability-item"
                    {...itemProps}
                  >
                    <div className="fl__day-detail-panel__item-indicator fl__day-detail-panel__item-indicator--green" />
                    <div className="fl__day-detail-panel__item-content">
                      <div className="fl__day-detail-panel__item-time">
                        {formatUtcToLocal(item.start_time, 'h:mm a')} -{' '}
                        {formatUtcToLocal(item.end_time, 'h:mm a')}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {hasAnyItems && sortedOpponentAvailability.length === 0 && (
          <div className="fl__day-detail-panel__section">
            <h4 className="fl__day-detail-panel__section-title">
              Opponent Availability (0)
            </h4>
            <p className="fl__day-detail-panel__no-items">
              No opponent availability
            </p>
          </div>
        )}

        {sortedOpponentAvailability.length > 0 && (
          <div className="fl__day-detail-panel__section">
            <h4 className="fl__day-detail-panel__section-title">
              Opponent Availability ({sortedOpponentAvailability.length})
            </h4>
            <div className="fl__day-detail-panel__items">
              {sortedOpponentAvailability.map((item) => {
                const opponentName = getOpponentName(item.opponent_user_id)
                const isInteractive = !!onOpponentAvailabilityClick
                const itemProps = getInteractiveItemProps(
                  onOpponentAvailabilityClick,
                  item,
                )

                return (
                  <div
                    key={item.id}
                    className={`fl__day-detail-panel__item fl__day-detail-panel__item--opponent-availability${
                      isInteractive
                        ? ' fl__day-detail-panel__item--interactive'
                        : ''
                    }`}
                    data-testid="opponent-availability-item"
                    {...itemProps}
                  >
                    <div className="fl__day-detail-panel__item-indicator fl__day-detail-panel__item-indicator--purple" />
                    <div className="fl__day-detail-panel__item-content">
                      <div className="fl__day-detail-panel__item-time">
                        {formatUtcToLocal(item.start_time, 'h:mm a')} -{' '}
                        {formatUtcToLocal(item.end_time, 'h:mm a')}
                      </div>
                      <div className="fl__day-detail-panel__item-meta">
                        {opponentName}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {hasAnyItems && sortedProposals.length === 0 && (
          <div className="fl__day-detail-panel__section">
            <h4 className="fl__day-detail-panel__section-title">
              Match Proposals (0)
            </h4>
            <p className="fl__day-detail-panel__no-items">No proposals</p>
          </div>
        )}

        {sortedProposals.length > 0 && (
          <div className="fl__day-detail-panel__section">
            <h4 className="fl__day-detail-panel__section-title">
              Match Proposals ({sortedProposals.length})
            </h4>
            <div className="fl__day-detail-panel__items">
              {sortedProposals.map((proposal) => {
                const isInteractive = !!onProposalAction
                const itemProps = getInteractiveItemProps(
                  onProposalAction,
                  proposal,
                )

                return (
                  <div
                    key={proposal.id}
                    className={`fl__day-detail-panel__item fl__day-detail-panel__item--proposal${
                      isInteractive
                        ? ' fl__day-detail-panel__item--interactive'
                        : ''
                    }`}
                    data-testid="proposal-item"
                    {...itemProps}
                  >
                    <div className="fl__day-detail-panel__item-indicator fl__day-detail-panel__item-indicator--blue" />
                    <div className="fl__day-detail-panel__item-content">
                      <div className="fl__day-detail-panel__item-time">
                        {formatUtcToLocal(proposal.proposed_time, 'h:mm a')}
                      </div>
                      <div className="fl__day-detail-panel__item-meta">
                        {getProposalStatusBadge(proposal.status)}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {hasAnyItems && sortedMatches.length === 0 && (
          <div className="fl__day-detail-panel__section">
            <h4 className="fl__day-detail-panel__section-title">
              Scheduled Matches (0)
            </h4>
            <p className="fl__day-detail-panel__no-items">No matches</p>
          </div>
        )}

        {sortedMatches.length > 0 && (
          <div className="fl__day-detail-panel__section">
            <h4 className="fl__day-detail-panel__section-title">
              Scheduled Matches ({sortedMatches.length})
            </h4>
            <div className="fl__day-detail-panel__items">
              {sortedMatches.map((match) => {
                const isInteractive = !!onMatchClick
                const itemProps = getInteractiveItemProps(onMatchClick, match)

                return (
                  <div
                    key={match.id}
                    className={`fl__day-detail-panel__item fl__day-detail-panel__item--match${
                      isInteractive
                        ? ' fl__day-detail-panel__item--interactive'
                        : ''
                    }`}
                    data-testid="match-item"
                    {...itemProps}
                  >
                    <div className="fl__day-detail-panel__item-indicator fl__day-detail-panel__item-indicator--orange" />
                    <div className="fl__day-detail-panel__item-content">
                      <div className="fl__day-detail-panel__item-time">
                        {formatUtcToLocal(match.match_datetime, 'h:mm a')}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>

      <div className="fl__day-detail-panel__footer">
        <p className="fl__day-detail-panel__footer-note">
          Add time blocks for {dateDisplay} so opponents can match around your
          schedule.
        </p>
        <button
          className="fl__day-detail-panel__add-button"
          onClick={onAddAvailability}
          aria-label="Add availability for selected date"
          data-testid="day-detail-add-availability-button"
        >
          <span
            className="fl__day-detail-panel__add-button-icon"
            aria-hidden="true"
          >
            +
          </span>
          <span className="fl__day-detail-panel__add-button-text">
            Add Availability
          </span>
        </button>
      </div>
    </div>
  )
}

DayDetailPanel.propTypes = {
  selectedDate: PropTypes.instanceOf(Date),
  availability: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      start_time: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]).isRequired,
      end_time: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]).isRequired,
    }),
  ),
  opponentAvailability: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      start_time: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]).isRequired,
      end_time: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]).isRequired,
      opponent_user_id: PropTypes.number.isRequired,
    }),
  ),
  unplayedOpponents: PropTypes.arrayOf(
    PropTypes.shape({
      user_id: PropTypes.number.isRequired,
      first_name: PropTypes.string.isRequired,
      last_name: PropTypes.string.isRequired,
    }),
  ),
  proposals: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      proposed_time: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]).isRequired,
      status: PropTypes.string.isRequired,
    }),
  ),
  matches: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      match_datetime: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]).isRequired,
    }),
  ),
  onClose: PropTypes.func.isRequired,
  onAddAvailability: PropTypes.func.isRequired,
  onEditAvailability: PropTypes.func,
  onOpponentAvailabilityClick: PropTypes.func,
  onProposalAction: PropTypes.func,
  onMatchClick: PropTypes.func,
}

export default DayDetailPanel
